'use client';

import { Fragment } from 'react';
import { usePathname } from 'next/navigation';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { Link } from '@/i18n/navigation';
import { dur, ease, sceneContainer, sceneItem, spring, springFade, stagger } from '@/lib/motion';
import { siteLinks, type SiteLinkKey } from '@/lib/site-links';

/**
 * Sitenin 404'ü — koyu blokta, hero'nun devamı gibi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden hero dili, sade bir hata kutusu değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Buraya düşen kişi çoğunlukla DIŞARIDAN geliyor: eski bir bağlantı, arama
 * sonucu, yanlış yazılmış bir adres. Yani bu sayfa sitenin ilk izlenimi olma
 * ihtimali yüksek bir sayfa. Next'in çıplak "404 | This page could not be
 * found" ekranı o ilk izlenimi "site yarım" diye veriyordu.
 *
 * DESIGN.md §4 nerede cesur olunacağını sayıyor: landing ve BOŞ DURUMLAR.
 * Burası ikisinin kesişimi — o yüzden ekran anasayfanın koyu bloğunu,
 * rampasını ve dalga alanını olduğu gibi kullanıyor. Yeni bir görsel dil
 * üretilmedi; kullanıcı aynı sitede olduğunu ilk karede anlıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Hareket AÇIKLIYOR: adres izi
 * ═══════════════════════════════════════════════════════════════════════════
 * Sayfanın merkezinde dev bir "404" rakamı yok. Onun yerine kullanıcının
 * İSTEDİĞİ adres, segment segment yazılıyor ve soldan sağa sırayla geliyor:
 *
 *     /  tr  /  fiyatlar  /  kurumsal
 *                            ^^^^^^^^ üstü çizilen son segment
 *
 * Son segment yerine oturduktan sonra üstüne bir çizgi çekiliyor (soldan
 * sağa, `scaleX`). Anlatılan şey tam olarak şu: "adresini buraya kadar
 * takip ettik, son adımda bir sayfa yok." DESIGN.md §4 hareketin bir şey
 * açıklamasını istiyor; buradaki jest sayfanın TEK cümlesini görselleştiriyor.
 *
 * Yararı süsün ötesinde: yazım hatası olan bir adreste kullanıcı hatayı
 * kendi gözüyle görüyor (`/fiyatlanidrma` gibi). Bu yüzden iz gerçek yoldan
 * okunuyor, dekoratif bir metin değil.
 *
 * ⚠️ Yol `next/navigation`'ın `usePathname`i ile okunuyor, `@/i18n/navigation`
 * ile DEĞİL: oradaki sarmalayıcı dil önekini ve çalışma alanı segmentini
 * söküyor, yani kullanıcının yazdığı adresi değil bizim iç biçimimizi
 * verirdi. Burada gösterilecek olan tam olarak adres çubuğundaki dize.
 *
 * ---------------------------------------------------------------------------
 * Gösterilen iz KIRPILIYOR — adres kullanıcıdan geliyor
 * ---------------------------------------------------------------------------
 * Yol bir girdi: 80 segmentlik ya da 5.000 karakterlik bir adres yazılabilir.
 * React metni kaçırdığı için güvenlik sorunu yok ama DÜZEN sorunu var; bu
 * yüzden segment sayısı ve segment uzunluğu kırpılıyor (aşağıdaki iki sabit).
 * Kırpılan yerde "…" duruyor, yani kullanıcı adresinin kısaltıldığını görüyor.
 *
 * ---------------------------------------------------------------------------
 * Bağlantılar `siteLinks`ten ve `live` süzgecinden geçerek
 * ---------------------------------------------------------------------------
 * 404'ten 404'e göndermek bu ekranın yapabileceği en kötü şey. Harita zaten
 * "sayfa gerçekten var mı" bilgisini taşıyor (`lib/site-links.ts`), o yüzden
 * liste oradan okunuyor ve `live: false` olan kayıt basılmıyor. Yeni bir sayfa
 * yayına girdiğinde buraya dokunmak gerekmiyor.
 *
 * Etiketler de yeni yazılmadı: header ve footer'ın kullandığı anahtarların
 * aynısı. Aynı sayfanın adı sitenin iki yerinde farklı olmamalı.
 */

/* Butonlar hero'nunkilerle AYNI: `.new-button-primary` / `.new-button-outline`
   vendor CSS'i `[data-v-d0b8df6f]` niteliğine bağlı (gerekçe hero.tsx başında),
   o yüzden nitelik burada da basılıyor. Yeni bir düğme derisi yazmak, aynı
   koyu yüzeyde ikinci bir düğme dili doğururdu. */
const V = 'd0b8df6f';

/**
 * Alt satırdaki kısayollar. Etiketler header/footer ile ORTAK — aynı sayfanın
 * adı sitenin iki yerinde farklı olmamalı.
 *
 * Sıra "kayıp ziyaretçi neyi arıyordu" sırası: önce ürünün ne olduğu ve neye
 * mal olduğu, sonra nasıl kullanıldığı, en sonda biz kimiz. Değişiklik günlüğü
 * listede YOK; 404'e düşen biri sürüm notu aramıyor.
 */
const JUMP: readonly { key: SiteLinkKey; label: string }[] = [
  { key: 'pricing', label: 'header.pricing' },
  { key: 'help', label: 'header.menu.help.title' },
  { key: 'blog', label: 'header.menu.blog.title' },
  { key: 'about', label: 'footer.company.about' },
  { key: 'contact', label: 'footer.company.contact' },
];

/** İzde gösterilen en fazla segment; fazlası ortadan "…" ile kırpılır. */
const MAX_SEGMENTS = 4;
/** Tek bir segmentin en fazla karakteri — 390px'te satırı taşırmayan ölçü. */
const MAX_CHARS = 40;

const ELLIPSIS = '…';

function clip(segment: string): string {
  return segment.length > MAX_CHARS ? `${segment.slice(0, MAX_CHARS - 1)}${ELLIPSIS}` : segment;
}

/**
 * `/tr/fiyatlar/kurumsal` → `['tr', 'fiyatlar', 'kurumsal']`
 *
 * Uzun adreste BAŞ ve SON korunuyor, ortası atılıyor: kullanıcı hem hangi
 * dilde olduğunu hem de hangi adımda takıldığını görmeli. Son segment ayrıca
 * ölü segment, yani atılması izin anlamını yok ederdi.
 */
function trailSegments(pathname: string): string[] {
  const raw = pathname.split('/').filter(Boolean);
  if (raw.length <= MAX_SEGMENTS) return raw.map(clip);

  return [clip(raw[0] ?? ''), ELLIPSIS, ...raw.slice(-2).map(clip)];
}

/* Segmentler soldan sağa geliyor; gecikme `staggerChildren` ile değil kendi
   sırasıyla (`custom={index}`) çünkü üstü çizilen çizginin de aynı sayıdan
   türeyen bir gecikmesi var — ikisini tek kaynaktan yürütmek gerekiyordu. */
const trailSeg: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: (index: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { ...springFade(spring.snap), delay: index * stagger.tight },
  }),
};

/* Çizgi son segment yerine OTURDUKTAN sonra çekiliyor: önce gelirse "burası
   yok" demeden önce siliyormuş gibi olur. Tween, çünkü çizginin fiziksel bir
   kütlesi yok (DESIGN.md §5) ve yay burada kamçı gibi görünüyordu. */
const trailStrike: Variants = {
  hidden: { scaleX: 0 },
  show: (count: number = 0) => ({
    scaleX: 1,
    transition: { delay: count * stagger.tight + dur.instant, duration: dur.fast, ease: ease.out },
  }),
};

export function NotFoundScene() {
  const t = useTranslations();
  const pathname = usePathname();
  const segments = trailSegments(pathname);

  /* Bölüm kabının kendi sınıfı YOK: katmanı `.dark-block > :not(.dark-block-waves)`
     kuralından geliyor (globals.css), ölçüleri de `.nf-inner`da. Boş bir sınıf
     adı yazmak CSS'te karşılığı varmış izlenimi verirdi. */
  return (
    <section>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div
            className="nf-inner"
            variants={sceneContainer}
            initial="hidden"
            animate="show"
          >
            {/* Kod bir rozet değil künye: yanındaki kısa hat sayfanın ızgara
                dilinden (hero kenarları, marka şeridi, SSS hatları). */}
            <motion.p className="nf-code" variants={sceneItem}>
              {t('notFound.site.code')}
            </motion.p>

            <motion.h1 className="nf-title" variants={sceneItem}>
              <span className="nf-title-line">{t('notFound.site.titleLead')}</span>
              <span className="nf-title-line nf-title-line--accent">
                {t('notFound.site.titleAccent')}
              </span>
            </motion.h1>

            <motion.p className="nf-lead" variants={sceneItem}>
              {t('notFound.site.lead')}
            </motion.p>

            {segments.length > 0 ? (
              <motion.p className="nf-trail" variants={sceneItem}>
                {/* Ekran okuyucu için izin ne olduğu söyleniyor; üstü çizili
                    segment ona görsel olarak bir şey anlatmıyor. */}
                <span className="sr-only">{t('notFound.site.trailLabel')}: </span>

                {segments.map((segment, index) => {
                  const dead = index === segments.length - 1;

                  return (
                    <Fragment key={`${index}-${segment}`}>
                      <span className="nf-trail-sep" aria-hidden="true">
                        /
                      </span>
                      <motion.span
                        className={dead ? 'nf-trail-seg nf-trail-seg--dead' : 'nf-trail-seg'}
                        variants={trailSeg}
                        custom={index}
                      >
                        {segment}
                        {dead ? (
                          <motion.span
                            className="nf-trail-strike"
                            variants={trailStrike}
                            custom={segments.length}
                            aria-hidden="true"
                          />
                        ) : null}
                      </motion.span>
                    </Fragment>
                  );
                })}

                <span className="sr-only"> — {t('notFound.site.trailDead')}</span>
              </motion.p>
            ) : null}

            <motion.div className="nf-actions" variants={sceneItem}>
              <Link href="/" className="new-button-primary" data-v-d0b8df6f={V}>
                {t('notFound.site.home')}
              </Link>
              <Link
                href={siteLinks.contact.href}
                className="new-button-outline"
                data-v-d0b8df6f={V}
              >
                {t('notFound.site.contact')}
              </Link>
            </motion.div>

            <motion.nav
              className="nf-jump"
              aria-label={t('notFound.site.jumpLabel')}
              variants={sceneItem}
            >
              {JUMP.filter(({ key }) => siteLinks[key].live).map(({ key, label }) => (
                <Link key={key} href={siteLinks[key].href} className="nf-jump-link">
                  {t(label)}
                </Link>
              ))}
            </motion.nav>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
